import React, { useState, useEffect, useRef, useCallback } from 'react';
import { auth, db } from '../lib/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { Shield, CheckCircle, XCircle, Clock, RefreshCw, AlertCircle, X, Mail, ExternalLink, Home, MapPin, Phone } from 'lucide-react';
import { UserProfile } from '../types';
import InternationalPhoneInput from './shared/InternationalPhoneInput';
import { RichTextContent } from './shared/RichTextEditor';
import { fetchUserLegal, recordConsent, type PublicLegalView } from './legal/legalApi';
import StructuredAddressForm, { EMPTY_ADDRESS, flattenAddress, parseAddress } from './shared/StructuredAddressForm';
import type { AddressValues } from './shared/StructuredAddressForm';

interface DiditVerificationGateProps {
  userProfile: UserProfile;
  onComplete?: () => void;
  /** When true, renders as an overlay modal instead of a full-page gate */
  asModal?: boolean;
  /** Called when the user closes the modal without completing */
  onClose?: () => void;
}

type GateState = 'loading' | 'confirm' | 'creating' | 'pending' | 'in_progress' | 'submitted' | 'in_review' | 'approved' | 'declined' | 'expired' | 'abandoned' | 'kyc_expired' | 'reverification' | 'error';
type LinkSendState = 'idle' | 'sending' | 'sent' | 'error';

export default function DiditVerificationGate({ userProfile, onComplete, asModal, onClose }: DiditVerificationGateProps) {
  const [gateState, setGateState] = useState<GateState>('loading');
  const [sessionUrl, setSessionUrl] = useState<string | null>(null);
  // Wave 4 (Slice D D-03): biometric notice + written release BEFORE any
  // session is created. The notice/schedule text comes from the workspace
  // Legal config; when it is not configured, NO session is created and a
  // neutral "verification unavailable" state shows instead of collecting
  // biometrics with no notice. The rest of the application flow is not
  // blocked by verification status (unchanged behaviour), so this fails
  // safe without trapping the applicant.
  const [legalView, setLegalView] = useState<PublicLegalView | null>(null);
  const [legalLoaded, setLegalLoaded] = useState(false);
  const [biometricConsent, setBiometricConsent] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchUserLegal().then(v => { if (alive) { setLegalView(v); setLegalLoaded(true); } });
    return () => { alive = false; };
  }, []);
  const biometricNoticeHtml = legalView?.disclosures.biometricNotice?.html || '';
  const biometricScheduleHtml = legalView?.disclosures.biometricSchedule?.html || '';
  const biometricConfigured = legalLoaded && !!biometricNoticeHtml;
  const [errorMessage, setErrorMessage] = useState('');
  const [linkSendState, setLinkSendState] = useState<LinkSendState>('idle');
  const [linkSendError, setLinkSendError] = useState('');
  const [confirmFirstName, setConfirmFirstName] = useState(userProfile.firstName || '');
  const [confirmLastName, setConfirmLastName] = useState(userProfile.lastName || '');
  const [confirmPhone, setConfirmPhone] = useState(userProfile.phoneNumber || '');
  const [confirmAddress, setConfirmAddress] = useState<AddressValues>(() =>
    parseAddress(userProfile.address, userProfile as any)
  );
  const [isResuming, setIsResuming] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const mountedRef = useRef(true);

  // Cleanup Firestore listener on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, []);

  // Subscribe to Firestore didit.status changes for the current user
  const subscribeToDiditStatus = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    const userRef = doc(db, 'users', userProfile.uid);
    unsubscribeRef.current = onSnapshot(userRef, (snap) => {
      if (!mountedRef.current) return;
      const data = snap.data() as UserProfile | undefined;
      const status = data?.didit?.status;

      if (status === 'approved') {
        setGateState('approved');
        // Unsubscribe immediately so the listener doesn't linger during the
        // 1500ms transition or after onComplete triggers an unmount.
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
        setTimeout(() => {
          if (mountedRef.current) onComplete?.();
        }, 1500);
      } else if (status === 'declined') {
        setGateState('declined');
      } else if (status === 'expired') {
        setGateState('expired');
      } else if (status === 'in_review') {
        setGateState('in_review');
      } else if (status === 'abandoned') {
        setGateState('abandoned');
      } else if (status === 'kyc_expired') {
        setGateState('kyc_expired');
      } else if (status === 'in_progress') {
        setGateState('in_progress');
      }
    });
  }, [userProfile.uid, onComplete]);

  // Refresh session status from Didit API
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/auth/didit/retrieve-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error('Refresh failed:', data.error || res.statusText);
        return;
      }

      // If the session no longer exists on Didit side, transition to
      // the appropriate terminal state so the user can re-initiate.
      if (data.gone || data.status === 'expired') {
        setSessionUrl(null);
        setGateState('expired');
        return;
      }

      // If manually approved, move straight to completion so the gate resolves
      // even when there is no active Firestore listener (e.g. confirm state).
      if (data.status === 'approved') {
        setGateState('approved');
        setTimeout(() => {
          if (mountedRef.current) onComplete?.();
        }, 1500);
        return;
      }

      // Active session exists — transition the gate so the user can resume
      // straight away even when there is no Firestore listener active.
      if (data.status === 'pending' || data.status === 'in_progress') {
        if (data.sessionUrl) {
          setSessionUrl(data.sessionUrl);
          setIsResuming(true);
          setGateState('pending');
          subscribeToDiditStatus();
        } else {
          // Session is active but Didit no longer provides a URL — clear locally
          setSessionUrl(null);
          setGateState('expired');
        }
        return;
      }

      if (data.status === 'in_review') {
        setGateState('in_review');
        subscribeToDiditStatus();
        return;
      }

      // Terminal non-expired states (declined / abandoned / kyc_expired)
      if (data.status === 'declined' || data.status === 'abandoned' || data.status === 'kyc_expired') {
        setGateState(data.status);
        return;
      }

      // For anything truly unexpected, fall back to confirm so the user can start fresh
      setGateState('confirm');
    } catch (err) {
      console.error('Refresh error:', err);
    } finally {
      setRefreshing(false);
    }
  }, [onComplete]);

  // Create a Didit session (or resume an existing one)
  const createSession = useCallback(async ({ reverification = false } = {}) => {
    setGateState(reverification ? 'reverification' : 'creating');
    setErrorMessage('');
    setIsResuming(false);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/auth/didit/create-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to create verification session');
      }
      if (data.status === 'approved') {
        setGateState('approved');
        onComplete?.();
        return;
      }
      if (data.sessionUrl) {
        setSessionUrl(data.sessionUrl);
        setIsResuming(data.isNew === false);
        setGateState('pending');
        subscribeToDiditStatus();
      } else {
        throw new Error('No session URL returned');
      }
    } catch (err: any) {
      if (!mountedRef.current) return;
      setErrorMessage(err.message || 'Network error. Please try again.');
      setGateState('error');
    }
  }, [onComplete, subscribeToDiditStatus]);

  // Save profile name to server — called on blur so the profile stays in sync
  const saveProfileName = useCallback(async (firstName: string, lastName: string) => {
    const fName = firstName.trim();
    const lName = lastName.trim();
    if (!fName || !lName) return;
    if (fName === (userProfile.firstName || '') && lName === (userProfile.lastName || '')) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      await fetch('/api/profile/update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ firstName: fName, lastName: lName }),
      });
    } catch {
      // Silently fail — session creation will send whatever is in the inputs
    }
  }, [userProfile.firstName, userProfile.lastName]);

  // Save profile address to server — called on blur so the profile stays in sync
  const saveProfileAddress = useCallback(async (addr: AddressValues) => {
    const flat = flattenAddress(addr);
    const hasChanged = flat !== (userProfile.address || '') ||
      addr.addressLine1 !== ((userProfile as any).addressLine1 || '');
    if (!hasChanged) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      await fetch('/api/profile/update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          address: flat,
          addressLine1: addr.addressLine1,
          addressLine2: addr.addressLine2,
          city: addr.city,
          state: addr.state,
          country: addr.country,
          postalCode: addr.postalCode,
        }),
      });
    } catch {
      // Silently fail
    }
  }, [userProfile]);

  // Save profile phone number to server — called on blur so the profile stays in sync
  const saveProfilePhone = useCallback(async (e164: string) => {
    if (e164 === (userProfile.phoneNumber || '')) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      await fetch('/api/profile/update', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ phoneNumber: e164 }),
      });
    } catch {
      // Silently fail
    }
  }, [userProfile.phoneNumber]);

  // Handle the "proceed" action from the confirm screen — record the
  // biometric written release FIRST (server-side evidence), then create
  // the session. No consent record → no session → no capture.
  const handleConfirmAndProceed = useCallback(async (reverification = false) => {
    if (!biometricConfigured || !biometricConsent) return;
    try {
      await recordConsent({
        kind: 'biometricNotice',
        version: legalView?.disclosures.biometricNotice?.version ?? 0,
      });
    } catch {
      setErrorMessage('We could not record your consent, so verification cannot start yet. Please try again.');
      return;
    }
    createSession({ reverification });
  }, [createSession, biometricConfigured, biometricConsent, legalView]);

  // Determine initial state on mount — with session recovery
  useEffect(() => {
    const existingStatus = userProfile.didit?.status;
    if (existingStatus === 'approved') {
      setGateState('approved');
      onComplete?.();
      return;
    }
    if (existingStatus === 'reverification_required') {
      setGateState('confirm');
      return;
    }
    if (existingStatus === 'pending' || existingStatus === 'in_progress') {
      // Active session awaiting completion
      setSessionUrl(userProfile.didit?.sessionUrl || null);
      setIsResuming(true);
      setGateState('pending');
      subscribeToDiditStatus();
      return;
    }
    if (existingStatus === 'in_review') {
      // Under manual review
      setGateState('in_review');
      subscribeToDiditStatus();
      return;
    }
    if (existingStatus === 'declined' || existingStatus === 'kyc_expired') {
      setGateState(existingStatus);
      return;
    }
    if (existingStatus === 'expired' || existingStatus === 'abandoned') {
      // Start fresh on confirmation rather than showing an expired error dead-end
      setGateState('confirm');
      return;
    }
    // No existing session — show confirmation first
    setGateState('confirm');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetry = () => {
    // Reset name fields from profile in case they changed
    setConfirmFirstName(userProfile.firstName || '');
    setConfirmLastName(userProfile.lastName || '');
    setGateState('confirm');
  };

  const handleSendLink = async () => {
    setLinkSendState('sending');
    setLinkSendError('');
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/auth/didit/send-verification-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sessionUrl,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to send verification link');
      }
      setLinkSendState('sent');
    } catch (err: any) {
      if (!mountedRef.current) return;
      setLinkSendError(err.message || 'Failed to send link. Please try again.');
      setLinkSendState('error');
    }
  };

  // ── Wrapper for content ──
  const content = (
    <>
      {/* Loading / Creating / Resuming */}
      {(gateState === 'loading' || gateState === 'creating' || gateState === 'reverification') && (
        <div className="flex flex-col items-center justify-center p-6 sm:p-8 md:p-10 text-center" role="status" aria-live="polite">
          <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-blue-50 flex items-center justify-center mb-4 sm:mb-5 ring-1 ring-blue-100">
            <Shield className="h-7 w-7 sm:h-8 sm:w-8 text-brand" aria-hidden="true" />
          </div>
          <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-2">Identity Verification</h2>
          <p className="text-xs sm:text-sm text-slate-600 mb-5 sm:mb-6 max-w-sm mx-auto leading-relaxed px-2">
            {gateState === 'reverification'
              ? 'An administrator has requested a new identity verification. Please complete the process to continue.'
              : isResuming
                ? 'Found an existing verification session. You can pick up where you left off.'
                : 'You can continue exploring the platform, but you must complete identity verification before submitting any application.'}
          </p>
          <div className="flex flex-col items-center gap-3">
            <div className="premium-loader" aria-hidden="true" />
            <p className="text-[11px] sm:text-xs text-slate-500 font-semibold">
              {gateState === 'creating' || gateState === 'reverification' ? 'Preparing verification session...' : isResuming ? 'Resuming session...' : 'Loading...'}
            </p>
          </div>
        </div>
      )}

      {/* Wave 4 fail-safe: verification not configured with a biometric
          notice → neutral unavailable state. No session, no capture. The
          applicant can close this and keep using the rest of the portal;
          submission is not blocked by verification status today. */}
      {gateState === 'confirm' && legalLoaded && !biometricConfigured && (
        <div className="p-6 sm:p-8 md:p-10 text-center" role="status">
          <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-slate-50 flex items-center justify-center mb-4 sm:mb-5 ring-1 ring-slate-200">
            <Shield className="h-7 w-7 sm:h-8 sm:w-8 text-slate-400" aria-hidden="true" />
          </div>
          <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-2">Identity verification is not available yet</h2>
          <p className="text-xs sm:text-sm text-slate-600 max-w-sm mx-auto leading-relaxed">
            Verification is paused while its privacy notice is being finalised. No information has been sent
            anywhere. You can keep working on your application — please contact support if you need to verify
            your identity urgently.
          </p>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-slate-600 transition-all cursor-pointer disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Checking...' : 'Already verified? Refresh'}
          </button>
        </div>
      )}

      {/* Confirm Name — ask user to verify their name before proceeding */}
      {gateState === 'confirm' && (!legalLoaded || biometricConfigured) && (
        <div className="p-6 sm:p-8 md:p-10 text-center">
          <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-blue-50 flex items-center justify-center mb-4 sm:mb-5 ring-1 ring-blue-100">
            <Shield className="h-7 w-7 sm:h-8 sm:w-8 text-brand" aria-hidden="true" />
          </div>
          <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-1">Verify Your Identity</h2>
          <p className="text-xs sm:text-sm text-slate-600 mb-5 sm:mb-6 max-w-sm mx-auto leading-relaxed px-2">
            Please confirm your details below. Your name must match your government-issued ID,
            and your home address is where we can send program-related documents if needed.
          </p>
          <div className="max-w-sm mx-auto space-y-4 mb-6">
            {/* First & Last Name */}
            <div className="grid grid-cols-2 gap-3">
              <div className="text-left">
                <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1.5">
                  First Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={confirmFirstName}
                  onChange={(e) => setConfirmFirstName(e.target.value)}
                  onBlur={() => saveProfileName(confirmFirstName, confirmLastName)}
                  placeholder="First name"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-brand-primary transition-all"
                />
              </div>
              <div className="text-left">
                <label className="block text-[11px] font-bold text-slate-500 uppercase mb-1.5">
                  Last Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={confirmLastName}
                  onChange={(e) => setConfirmLastName(e.target.value)}
                  onBlur={() => saveProfileName(confirmFirstName, confirmLastName)}
                  placeholder="Last name"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-brand-primary transition-all"
                />
              </div>
            </div>

            {/* Phone Number — International Phone Input */}
            <div className="text-left">
              <InternationalPhoneInput
                value={confirmPhone}
                countryCode={(userProfile as any).country}
                onChange={(e164) => setConfirmPhone(e164)}
                onBlur={() => saveProfilePhone(confirmPhone)}
                label="Phone Number"
                required
              />
            </div>

            {/* Home Address — Structured Address Form */}
            <div className="text-left">
              <div className="flex items-center gap-1.5 mb-2">
                <Home className="h-3.5 w-3.5 text-slate-400" />
                <label className="text-[11px] font-bold text-slate-500 uppercase">
                  Home Address <span className="text-red-400">*</span>
                </label>
              </div>
              <StructuredAddressForm
                values={confirmAddress}
                onChange={setConfirmAddress}
                onBlur={() => saveProfileAddress(confirmAddress)}
                showIcons={false}
              />
            </div>
          </div>
          {/* Wave 4 biometric notice + written release (BIPA §15 pattern):
              the notice names the provider (Didit), states purpose and the
              retention/destruction position, and the unticked checkbox is
              the written release. The session is created only after the
              consent is recorded server-side. */}
          <div className="max-w-sm mx-auto text-left bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4">
            <div className="text-[11px] sm:text-xs text-slate-700 leading-relaxed max-h-44 overflow-y-auto">
              <RichTextContent html={biometricNoticeHtml} />
              {biometricScheduleHtml && (
                <details className="mt-2">
                  <summary className="font-semibold cursor-pointer">Retention &amp; destruction schedule</summary>
                  <div className="mt-1"><RichTextContent html={biometricScheduleHtml} /></div>
                </details>
              )}
            </div>
            <label htmlFor="didit-biometric-consent" className="flex items-start gap-2 mt-3 cursor-pointer">
              <input
                id="didit-biometric-consent"
                type="checkbox"
                checked={biometricConsent}
                onChange={e => setBiometricConsent(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0"
              />
              <span className="text-[11px] sm:text-xs font-semibold text-slate-800 leading-relaxed">
                I consent to the collection, use and disclosure to Didit of my biometric identifiers and
                biometric information as described above.
              </span>
            </label>
          </div>
          <button
            onClick={() => handleConfirmAndProceed()}
            disabled={!confirmFirstName.trim() || !confirmLastName.trim() || !confirmAddress.addressLine1.trim() || !biometricConsent}
            className="inline-flex items-center gap-2 px-6 py-2.5 sm:py-3 btn-primary  disabled:bg-slate-300 text-white rounded-xl text-xs sm:text-sm font-semibold transition-all shadow-lg shadow-blue-600/20 cursor-pointer disabled:cursor-not-allowed active:scale-[0.98]"
          >
            <Shield className="h-4 w-4" />
            {isResuming ? 'Resume Verification' : 'Confirm & Continue'}
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-slate-600 transition-all cursor-pointer disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Checking...' : 'Already verified? Refresh'}
          </button>
          <p className="text-[10px] sm:text-[11px] text-slate-400 mt-1">
            Your details will be sent to Didit, our identity verification provider, only after you consent above.
          </p>
        </div>
      )}

      {/* Pending (iFrame) */}
      {gateState === 'pending' && sessionUrl && (
        <div className="flex flex-col min-h-0">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-100 flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-blue-50 flex items-center justify-center ring-1 ring-blue-100 shrink-0">
              <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-brand" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-bold text-slate-900 truncate">Identity Verification</h2>
              <p className="text-[10px] sm:text-[11px] text-slate-500 truncate">{isResuming ? 'Resuming your existing verification session' : 'Complete the verification process below'}</p>
            </div>
            <div className="ml-auto flex items-center gap-1.5 shrink-0">
              <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-500" aria-hidden="true" />
              <span className="text-[10px] sm:text-[11px] font-semibold text-amber-600 whitespace-nowrap">{isResuming ? 'Resuming...' : 'Awaiting verification...'}</span>
            </div>
          </div>
          <div className="relative w-full flex-1 min-h-[360px] sm:min-h-[440px] md:min-h-[500px] max-h-[65vh] sm:max-h-[70vh]" style={{ height: asModal ? 'auto' : '520px' }}>
            <iframe
              ref={iframeRef}
              src={sessionUrl}
              title="Didit Identity Verification"
              className="absolute inset-0 w-full h-full border-0"
              allow="camera; microphone; publickey-credentials-get"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              style={{ maxWidth: '100%' }}
            />
          </div>
          <div className="px-4 sm:px-6 py-2.5 sm:py-3 bg-slate-50 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="text-[10px] sm:text-[11px] font-semibold text-brand hover:text-brand transition-all cursor-pointer flex items-center gap-1 disabled:opacity-50"
              >
                <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Checking...' : 'Refresh'}
              </button>
              {/* Send link to email */}
              {linkSendState === 'idle' && (
                <button
                  onClick={handleSendLink}
                  className="text-[10px] sm:text-[11px] font-semibold text-slate-600 hover:text-slate-800 transition-all cursor-pointer flex items-center gap-1"
                >
                  <Mail className="h-3 w-3" />
                  <span className="hidden xs:inline">Send Link to My Email</span>
                  <span className="xs:hidden">Email Link</span>
                </button>
              )}
              {linkSendState === 'sending' && (
                <span className="text-[10px] sm:text-[11px] text-slate-400 flex items-center gap-1">
                  <RefreshCw className="h-3 w-3 animate-spin" />
                  Sending...
                </span>
              )}
              {linkSendState === 'sent' && (
                <span className="text-[10px] sm:text-[11px] text-emerald-600 font-semibold flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Link sent! Check your email.
                </span>
              )}
              {linkSendState === 'error' && (
                <span className="text-[10px] sm:text-[11px] text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {linkSendError || 'Failed to send'}
                </span>
              )}
            </div>
            <p className="text-[10px] sm:text-[11px] text-slate-400">Complete on any device</p>
          </div>
          <div className="sr-only" aria-live="assertive" role="status">
            Identity verification is in progress. Complete the verification form to continue.
          </div>
        </div>
      )}

      {/* In Review — pending manual approval by Didit */}
      {gateState === 'in_review' && (
        <div className="p-6 sm:p-8 md:p-10 text-center">
          <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-amber-50 flex items-center justify-center mb-4 sm:mb-5 ring-1 ring-amber-100">
            <Clock className="h-7 w-7 sm:h-8 sm:w-8 text-amber-500" aria-hidden="true" />
          </div>
          <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-2">Verification Submitted</h2>
          <p className="text-xs sm:text-sm text-slate-600 mb-5 sm:mb-6 max-w-sm mx-auto leading-relaxed px-2">
            We have received your identity verification submission. It has been sent for manual review
            and requires approval. This process may take some time. You will be notified once the review is complete.
          </p>
          <div className="flex justify-center mb-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 border border-amber-200 rounded-xl">
              <Clock className="h-4 w-4 text-amber-500" />
              <span className="text-xs font-semibold text-amber-700">Pending Manual Approval</span>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl text-xs font-semibold transition-all cursor-pointer disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Checking...' : 'Refresh'}
          </button>
          <div className="sr-only" aria-live="assertive" role="status">
            Your identity verification has been submitted and is pending manual review. You will be notified once approved.
          </div>
        </div>
      )}

      {/* Declined */}
      {gateState === 'declined' && (
        <div className="p-6 sm:p-8 md:p-10 text-center">
          <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4 sm:mb-5 ring-1 ring-red-100">
            <XCircle className="h-7 w-7 sm:h-8 sm:w-8 text-red-500" aria-hidden="true" />
          </div>
          <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-2">Verification Failed</h2>
          <p className="text-xs sm:text-sm text-slate-600 mb-5 sm:mb-6 max-w-sm mx-auto leading-relaxed px-2">
            Your identity verification was not approved. This can happen if the information
            provided doesn't match official records. You can retry the verification process.
          </p>
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 btn-primary  text-white rounded-xl text-xs sm:text-sm font-semibold transition-all shadow-lg shadow-blue-600/20 cursor-pointer active:scale-[0.98]"
          >
            <RefreshCw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Retry Verification
          </button>
          <div className="sr-only" aria-live="assertive" role="status">
            Your identity verification was declined. You can retry by clicking the button above.
          </div>
        </div>
      )}

      {/* Expired */}
      {gateState === 'expired' && (
        <div className="p-6 sm:p-8 md:p-10 text-center">
          <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-orange-50 flex items-center justify-center mb-4 sm:mb-5 ring-1 ring-orange-100">
            <Clock className="h-7 w-7 sm:h-8 sm:w-8 text-orange-500" aria-hidden="true" />
          </div>
          <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-2">Verification Session Expired</h2>
          <p className="text-xs sm:text-sm text-slate-600 mb-5 sm:mb-6 max-w-sm mx-auto leading-relaxed px-2">
            Your verification session timed out. Please start a new verification session.
          </p>
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 btn-primary  text-white rounded-xl text-xs sm:text-sm font-semibold transition-all shadow-lg shadow-blue-600/20 cursor-pointer active:scale-[0.98]"
          >
            <RefreshCw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Start New Verification
          </button>
          <div className="sr-only" aria-live="assertive" role="status">
            Your verification session expired. Please start a new verification.
          </div>
        </div>
      )}

      {/* Abandoned — user started but didn't finish */}
      {gateState === 'abandoned' && (
        <div className="p-6 sm:p-8 md:p-10 text-center">
          <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-orange-50 flex items-center justify-center mb-4 sm:mb-5 ring-1 ring-orange-100">
            <AlertCircle className="h-7 w-7 sm:h-8 sm:w-8 text-orange-500" aria-hidden="true" />
          </div>
          <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-2">Verification Not Completed</h2>
          <p className="text-xs sm:text-sm text-slate-600 mb-5 sm:mb-6 max-w-sm mx-auto leading-relaxed px-2">
            You started the verification process but did not complete it. You can restart
            verification at any time to pick up where you left off.
          </p>
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 btn-primary  text-white rounded-xl text-xs sm:text-sm font-semibold transition-all shadow-lg shadow-blue-600/20 cursor-pointer active:scale-[0.98]"
          >
            <RefreshCw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Restart Verification
          </button>
          <div className="sr-only" aria-live="assertive" role="status">
            Your verification was not completed. Please restart the verification process.
          </div>
        </div>
      )}

      {/* Kyc Expired — previously approved, now aged out */}
      {gateState === 'kyc_expired' && (
        <div className="p-6 sm:p-8 md:p-10 text-center">
          <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-slate-50 flex items-center justify-center mb-4 sm:mb-5 ring-1 ring-slate-200">
            <Clock className="h-7 w-7 sm:h-8 sm:w-8 text-slate-500" aria-hidden="true" />
          </div>
          <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-2">Verification Requires Renewal</h2>
          <p className="text-xs sm:text-sm text-slate-600 mb-5 sm:mb-6 max-w-sm mx-auto leading-relaxed px-2">
            Your previously approved identity verification has expired per policy.
            Please complete a new verification to continue.
          </p>
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 btn-primary  text-white rounded-xl text-xs sm:text-sm font-semibold transition-all shadow-lg shadow-blue-600/20 cursor-pointer active:scale-[0.98]"
          >
            <RefreshCw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Re-Verify Now
          </button>
          <div className="sr-only" aria-live="assertive" role="status">
            Your previous verification has expired. Please complete a new verification.
          </div>
        </div>
      )}

      {/* Approved */}
      {gateState === 'approved' && (
        <div className="p-6 sm:p-8 md:p-10 text-center">
          <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4 sm:mb-5 ring-1 ring-emerald-100">
            <CheckCircle className="h-7 w-7 sm:h-8 sm:w-8 text-emerald-500" aria-hidden="true" />
          </div>
          <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-2">Identity Verified</h2>
          <p className="text-xs sm:text-sm text-slate-600 mb-5 sm:mb-6 max-w-sm mx-auto leading-relaxed px-2">
            Your identity has been successfully verified. Redirecting...
          </p>
          <div className="flex justify-center">
            <div className="premium-loader" aria-hidden="true" />
          </div>
          <div className="sr-only" aria-live="assertive" role="status">
            Your identity has been verified successfully.
          </div>
        </div>
      )}

      {/* Error */}
      {gateState === 'error' && (
        <div className="p-6 sm:p-8 md:p-10 text-center">
          <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4 sm:mb-5 ring-1 ring-red-100">
            <AlertCircle className="h-7 w-7 sm:h-8 sm:w-8 text-red-500" aria-hidden="true" />
          </div>
          <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-2">Unable to Start Verification</h2>
          <p className="text-xs sm:text-sm text-slate-600 mb-4 max-w-sm mx-auto leading-relaxed px-2">
            {errorMessage || 'A temporary error occurred.'}
          </p>
          <button
            onClick={handleRetry}
            className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 btn-primary  text-white rounded-xl text-xs sm:text-sm font-semibold transition-all shadow-lg shadow-blue-600/20 cursor-pointer active:scale-[0.98]"
          >
            <RefreshCw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            Try Again
          </button>
          <p className="text-[10px] sm:text-[11px] text-slate-400 mt-4">
            You won't lose your account or application data.
          </p>
          <div className="sr-only" aria-live="assertive" role="status">
            {errorMessage || 'A temporary error occurred. Please try again.'}
          </div>
        </div>
      )}
    </>
  );

  if (asModal) {
    return (
      <>
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-2 sm:p-4" role="dialog" aria-modal="true" aria-label="Identity Verification">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
          <div className="relative bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-lg sm:max-w-xl lg:max-w-2xl max-h-[95dvh] sm:max-h-[90vh] overflow-y-auto animate-fade-in flex flex-col">
            {onClose && (
              <button
                onClick={onClose}
                className="absolute top-2 right-2 sm:top-3 sm:right-3 z-10 p-1.5 sm:p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all cursor-pointer"
                aria-label="Close verification"
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            )}
            {content}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-dvh premium-shell flex flex-col items-center justify-center p-4 sm:p-6 md:p-8 font-sans animate-fade-in bg-gradient-to-br from-slate-50 to-white">
      <div className="w-full max-w-lg sm:max-w-xl lg:max-w-2xl">
        {content}
      </div>
    </div>
  );
}
